import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { drawZoneStitches } from "../../based/vendor/dst-parser";

/**
 * Admin chọn màu kim (EMB_NeedleBars) cho từng zone của file DST tham chiếu (EMB_PatternEffects.RefDstFileUrl) -
 * zone đã được parse client-side (Tajima DST parser, xem PatternUploadModal._handleUploadRefDst), không cần
 * chờ server. Canvas mỗi zone là line-art vẽ trực tiếp từ toạ độ mũi thêu (drawZoneStitches), không phải
 * ảnh photorealistic (cái đó chỉ cần cho PNG preview cuối cùng của từng size, làm ở server/worker).
 *
 * props:
 * - zones: [{zoneIndex, stitchCount, stitches}] - kết quả parseDstZones
 * - docWidth/docHeight: kích thước tài liệu (px) để canvas scale đúng tỉ lệ
 * - zoneColors: [{zoneIndex, threadColorId, threadColorCode, threadColorName, threadColorHex}] - màu đã chọn
 * - onChange(zoneColors): callback khi đổi màu 1 zone - cập nhật state effect ở component cha
 * - needleColorGroups: [{machineId, machineName, colors:[{value, text, code, hexColor}]}]
 */
function EffectZoneColorPicker({ zones, docWidth, docHeight, zoneColors, onChange, needleColorGroups }) {
    const [activeZoneIndex, setActiveZoneIndex] = useState(zones && zones.length > 0 ? zones[0].zoneIndex : 0);
    const [groupId, setGroupId] = useState(0);

    useEffect(() => {
        if (needleColorGroups && needleColorGroups.length > 0 && !groupId) setGroupId(needleColorGroups[0].machineId);
    }, [needleColorGroups]);

    useEffect(() => {
        if (zones && zones.length > 0 && !zones.some((z) => z.zoneIndex == activeZoneIndex)) setActiveZoneIndex(zones[0].zoneIndex);
    }, [zones]);

    const activeGroup = useMemo(() => (needleColorGroups || []).find((x) => x.machineId == groupId) || (needleColorGroups && needleColorGroups[0]), [needleColorGroups, groupId]);

    const _colorOf = (zoneIndex) => {
        const found = (zoneColors || []).find((x) => x.zoneIndex == zoneIndex);
        return found ? found.threadColorHex : null;
    };

    const _selectColor = (color) => {
        const current = (zoneColors || []).find((x) => x.zoneIndex == activeZoneIndex);
        const zone = (zones || []).find((z) => z.zoneIndex == activeZoneIndex);
        let next = (zoneColors || []).filter((x) => x.zoneIndex != activeZoneIndex);
        //tap lại màu đang chọn = bỏ chọn (giữ convention của sheet đổi màu phía khách)
        if (!current || current.threadColorHex != color.hexColor) {
            next = [
                ...next,
                {
                    zoneIndex: activeZoneIndex,
                    stitchCount: zone ? zone.stitchCount : 0,
                    threadColorId: color.value,
                    threadColorCode: color.code,
                    threadColorName: color.text,
                    threadColorHex: color.hexColor,
                },
            ];
        }
        onChange(next);
    };

    return (
        <Wrapper>
            <div className="zone-list">
                {(zones || []).map((zone, idx) => {
                    const isActive = zone.zoneIndex == activeZoneIndex;
                    const hex = _colorOf(zone.zoneIndex);
                    return (
                        <div key={idx} className={`zone-item ${isActive ? "active" : ""}`} onClick={() => setActiveZoneIndex(zone.zoneIndex)}>
                            <ZoneCanvas zone={zone} hex={hex} docWidth={docWidth} docHeight={docHeight} />
                            <div className="zone-item-label">
                                Zone {zone.zoneIndex + 1}
                                {hex && <span className="zone-item-dot" style={{ background: hex }} />}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="zone-picker-row">
                <select className="form-control" value={groupId} onChange={(e) => setGroupId(parseInt(e.target.value, 10))}>
                    {(needleColorGroups || []).map((group, idx) => (
                        <option key={idx} value={group.machineId}>
                            {group.machineName}
                        </option>
                    ))}
                </select>
            </div>

            <div className="zone-swatches">
                {activeGroup && activeGroup.colors && activeGroup.colors.length > 0 ? (
                    activeGroup.colors.map((color, idx) => {
                        const isActive = _colorOf(activeZoneIndex) == color.hexColor;
                        return <div key={idx} className={`zone-swatch ${isActive ? "active" : ""}`} style={{ background: color.hexColor }} title={color.text} onClick={() => _selectColor(color)} />;
                    })
                ) : (
                    <div className="text-muted">Chưa có màu kim</div>
                )}
            </div>
        </Wrapper>
    );
}

function ZoneCanvas({ zone, hex, docWidth, docHeight }) {
    const canvasRef = useRef(null);
    useEffect(() => {
        drawZoneStitches(canvasRef.current, zone.stitches, hex, docWidth, docHeight);
    }, [zone, hex, docWidth, docHeight]);
    return <canvas ref={canvasRef} width={64} height={64} />;
}

export default EffectZoneColorPicker;

const Wrapper = styled.div`
    margin-top: 14px;
    padding: 12px;
    background: #f7f7f7;
    border-radius: 8px;

    .zone-list {
        display: flex;
        overflow-x: auto;
        padding-bottom: 6px;
        margin-bottom: 10px;
    }

    .zone-item {
        flex-shrink: 0;
        margin-right: 10px;
        cursor: pointer;
        text-align: center;
    }

    .zone-item canvas {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        border: 1.5px solid #dedede;
        background: #fff;
    }

    .zone-item.active canvas {
        border: 1.5px solid #f20e45;
    }

    .zone-item-label {
        margin-top: 4px;
        font-size: 11px;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .zone-item-dot {
        display: inline-block;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        margin-left: 4px;
        border: 1px solid #fff;
        box-shadow: 0 0 0 1px #ccc;
    }

    .zone-picker-row {
        max-width: 220px;
        margin-bottom: 10px;
    }

    .zone-swatches {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .zone-swatch {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border: 1px solid rgba(0, 0, 0, 0.1);
        cursor: pointer;
    }

    .zone-swatch.active {
        outline: 2px solid #f20e45;
        outline-offset: 1px;
    }
`;
